/**
 * Centralized date and time utility functions
 * Provides consistent date/time handling across the Go Sit Back platform
 */
export class DateUtils {
  /**
   * Parse an ISO date string (YYYY-MM-DD) as a local date to avoid timezone shifts
   * @param dateString - Date string in YYYY-MM-DD format
   * @returns Date object in local timezone
   */
  static parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Determines if a given date falls within US Central Daylight Time.
   * CDT runs from the second Sunday of March to the first Sunday of November.
   */
  private static isInDST(date: Date): boolean {
    const year = date.getFullYear()
    
    // March (month 2) - find second Sunday
    const marchFirst = new Date(year, 2, 1)
    const firstSundayOfMarch = 1 + (7 - marchFirst.getDay()) % 7
    const secondSundayOfMarch = firstSundayOfMarch + 7
    const dstStart = new Date(year, 2, secondSundayOfMarch, 2, 0, 0) // 2:00 AM
    
    // November (month 10) - find first Sunday
    const novFirst = new Date(year, 10, 1)
    const firstSundayOfNov = 1 + (7 - novFirst.getDay()) % 7
    const dstEnd = new Date(year, 10, firstSundayOfNov, 2, 0, 0) // 2:00 AM
    
    return date >= dstStart && date < dstEnd
  }

  /**
   * Combines date and time strings into an ISO 8601 timestamp for database storage.
   * 
   * IMPORTANT: This function interprets the time as US Central Time (America/Chicago)
   * since Go Sit Back operates in Texas. The returned ISO string is in UTC but 
   * correctly represents the user's intended local time.
   * 
   * @param dateString - Date string in YYYY-MM-DD format
   * @param timeString - Time string in HH:MM format (24-hour)
   * @returns ISO 8601 string in UTC, or null if inputs are invalid
   * 
   * @example
   * formatDateTime('2026-02-07', '14:00') // User wants 2:00 PM Central
   * // Returns '2026-02-07T20:00:00.000Z' (2:00 PM CST = 8:00 PM UTC in winter)
   * 
   * formatDateTime('2026-07-15', '14:00') // User wants 2:00 PM Central
   * // Returns '2026-07-15T19:00:00.000Z' (2:00 PM CDT = 7:00 PM UTC in summer)
   */
  static formatDateTime(dateString: string | undefined, timeString: string | undefined): string | null {
    if (!dateString || !timeString) return null
    
    // Parse date and time components
    const [year, month, day] = dateString.split('-').map(Number)
    const [hours, minutes] = timeString.split(':').map(Number)
    
    // Validate parsed values
    if ([year, month, day, hours, minutes].some(isNaN)) {
      return null
    }
    
    // Determine if this date is in CST (-06:00) or CDT (-05:00)
    const testDate = new Date(year, month - 1, day)
    const isDST = this.isInDST(testDate)
    const offset = isDST ? '-05:00' : '-06:00'
    
    // Format as ISO 8601 with timezone offset
    const paddedMonth = String(month).padStart(2, '0')
    const paddedDay = String(day).padStart(2, '0')
    const paddedHours = String(hours).padStart(2, '0')
    const paddedMinutes = String(minutes).padStart(2, '0')
    
    const localISOString = `${year}-${paddedMonth}-${paddedDay}T${paddedHours}:${paddedMinutes}:00${offset}`
    
    // Parse and convert to UTC ISO string for database storage
    const dateObj = new Date(localISOString)
    
    if (isNaN(dateObj.getTime())) {
      return null
    }
    
    return dateObj.toISOString()
  }

  /**
   * Format a date with various display options
   * @param date - Date object or ISO string
   * @param format - Display format: 'short', 'long', or 'iso'
   * @returns Formatted date string
   */
  static formatDate(date: Date | string | null | undefined, format: 'short' | 'long' | 'iso' = 'short'): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return 'Invalid Date';
    
    switch (format) {
      case 'short':
        return dateObj.toLocaleDateString();
      case 'long':
        return dateObj.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'iso':
        return dateObj.toISOString();
      default:
        return dateObj.toLocaleDateString();
    }
  }

  /**
   * Format a date for standard display across the app.
   * Always displays in US Central Time for consistency.
   * 
   * @param date - Date object, ISO string, or timestamp string
   * @returns Formatted date string (e.g., "Friday, February 7, 2025")
   */
  static formatDateLong(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    let dateObj: Date;
    if (typeof date === 'string') {
      // Check if it's a simple YYYY-MM-DD date string (no time component)
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // Parse as local date to avoid timezone shifts
        dateObj = this.parseLocalDate(date);
      } else {
        // Parse as full ISO timestamp
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }
    if (!dateObj || isNaN(dateObj.getTime())) return 'Invalid Date';
    
    // For YYYY-MM-DD strings, don't apply timezone (they're already local dates)
    // For ISO timestamps, apply Central Time timezone
    const isSimpleDateString = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
    
    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      ...(isSimpleDateString ? {} : { timeZone: 'America/Chicago' })
    });
  }

  /**
   * Format a date for SMS messages
   * Uses MM/DD/YYYY format for brevity in text messages
   * Example: "12/25/2023"
   * @param date - Date object, ISO string, or timestamp string
   * @returns Formatted date string in MM/DD/YYYY format
   */
  static formatDateShort(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    let dateObj: Date;
    if (typeof date === 'string') {
      // Check if it's a simple YYYY-MM-DD date string (no time component)
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // Parse as local date to avoid timezone shifts
        dateObj = this.parseLocalDate(date);
      } else {
        // Parse as full ISO timestamp
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }
    if (!dateObj || isNaN(dateObj.getTime())) return 'Invalid Date';
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Format a time string to display format.
   * Use this for raw HH:MM time strings (not timestamps).
   * 
   * @param time - Time string in HH:MM format (24-hour)
   * @returns Formatted time string with AM/PM (e.g., "2:00 PM")
   */
  static formatTime(time: string | null | undefined): string {
    if (!time) return 'N/A';
    if (typeof time !== 'string' || !time.includes(':')) return 'Invalid Time';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    if (isNaN(hour)) return 'Invalid Time';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  }

  /**
   * Advanced time formatting with more options
   * @param timeString - Time string in HH:MM format
   * @param options - Formatting options
   * @returns Formatted time string
   */
  static formatTimeAdvanced(timeString: string | null | undefined, options?: {
    hour12?: boolean;
    showMinutes?: boolean;
  }): string {
    if (!timeString) return 'N/A';
    if (typeof timeString !== 'string' || !timeString.includes(':')) return 'Invalid Time';
    const { hour12 = true, showMinutes = true } = options || {};
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: showMinutes ? '2-digit' : undefined,
      hour12
    });
  }

  /**
   * Format a Date or ISO timestamp to time display in US Central Time.
   * Use this for timestamps retrieved from the database (requested_date_time, date_time).
   * 
   * IMPORTANT: Always displays in America/Chicago timezone since Go Sit Back operates in Texas.
   * 
   * @param date - Date object or ISO timestamp string
   * @param showMinutes - Whether to show minutes (default: true)
   * @returns Formatted time string (e.g., "2:00 PM") in Central Time
   */
  static formatTimeFromDate(date: Date | string | null | undefined, showMinutes: boolean = true): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return 'Invalid Date';
    return dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: showMinutes ? '2-digit' : undefined,
      hour12: true,
      timeZone: 'America/Chicago' // Always display in Central Time
    });
  }

  /**
   * Check if a date string is valid
   * @param date - Date string to validate
   * @returns True if date is valid
   */
  static isValidDate(date: string): boolean {
    return !isNaN(Date.parse(date));
  }

  /**
   * Add days to a date
   * @param date - Base date
   * @param days - Number of days to add
   * @returns New date with added days
   */
  static addDays(date: Date, days: number): Date {
    // Use local time methods to match test expectations
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Check if two dates are the same day
   * @param date1 - First date
   * @param date2 - Second date
   * @returns True if dates are on the same day
   */
  static isSameDay(date1: Date, date2: Date): boolean {
    return date1.toDateString() === date2.toDateString();
  }

  /**
   * Calculate the number of days between two dates
   * @param startDate - Start date
   * @param endDate - End date
   * @returns Number of days between dates
   */
  static daysBetween(startDate: Date, endDate: Date): number {
    const timeDiff = endDate.getTime() - startDate.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  /**
   * Get a relative time description (e.g., "2 days ago", "in 3 hours")
   * @param date - Date to compare
   * @param relativeTo - Date to compare against (defaults to now)
   * @returns Relative time string
   */
  static getRelativeTime(date: Date, relativeTo: Date = new Date()): string {
    const diffMs = date.getTime() - relativeTo.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (Math.abs(diffDays) > 0) {
      return diffDays > 0 ? `in ${diffDays} day${diffDays > 1 ? 's' : ''}` : `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} ago`;
    }
    if (Math.abs(diffHours) > 0) {
      return diffHours > 0 ? `in ${diffHours} hour${diffHours > 1 ? 's' : ''}` : `${Math.abs(diffHours)} hour${Math.abs(diffHours) > 1 ? 's' : ''} ago`;
    }
    if (Math.abs(diffMinutes) > 0) {
      return diffMinutes > 0 ? `in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}` : `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) > 1 ? 's' : ''} ago`;
    }
    return 'now';
  }

  /**
   * Format a date/time with a concise absolute value plus relative time.
   * Example: "Fri, Jan 9, 1:59 PM (2 days ago)"
   * @param date - Date object or ISO string to format
   * @param relativeTo - Optional reference date for the relative portion (defaults to now)
   * @returns Formatted date/time with relative string
   */
  static formatDateTimeWithRelative(
    date: Date | string | null | undefined,
    relativeTo: Date = new Date()
  ): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return 'Invalid Date';

    const absolute = dateObj.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const relative = this.getRelativeTime(dateObj, relativeTo);
    return `${absolute} (${relative})`;
  }

  /**
   * Format a timestamptz string to user-friendly time format
   * @param timeString - ISO timestamp string
   * @returns Formatted time string (e.g., "2:30 pm") or "Not set" if null
   */
  static formatTimestamptzToTime(timeString: string | null): string {
    if (!timeString) return 'Not set';
    
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
    } catch (error) {
      console.warn('Failed to parse time:', timeString, error);
      return timeString;
    }
  }

  /**
   * Convert timestamptz to HTML time input format (HH:MM)
   * @param timeString - ISO timestamp string
   * @returns Time in HH:MM format or empty string if null
   */
  static formatTimestamptzForInput(timeString: string | null): string {
    if (!timeString) return '';
    
    try {
      const date = new Date(timeString);
      return date.toTimeString().slice(0, 5);
    } catch (error) {
      console.warn('Failed to parse time for input:', timeString, error);
      return '';
    }
  }

  /**
   * Format duration in seconds to readable format
   * @param seconds - Duration in seconds
   * @returns Formatted duration string (e.g., "1h 23m 45s")
   */
  static formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    
    return parts.join(' ');
  }

  /**
   * Format duration in minutes to readable format
   * @param minutes - Duration in minutes
   * @returns Formatted duration string (e.g., "1h 30m" or "45m")
   */
  static formatDurationByMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
    
    return parts.join(' ');
  }

  /**
   * Format minutes to readable time format
   * @param minutes - Duration in minutes
   * @returns Formatted time string (e.g., "1h 30m" or "45m")
   */
  static formatMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  /**
   * Check if a date/time is within the next 24 hours from now
   * @param dateString - Date string in YYYY-MM-DD format
   * @param timeString - Time string in HH:MM format (optional)
   * @returns True if the date/time is in the future and within 24 hours from now
   */
  static isWithin24Hours(dateString: string, timeString?: string): boolean {
    const now = new Date();
    // Parse as local date to avoid timezone issues
    const targetDate = this.parseLocalDate(dateString);
    
    // If time is provided, set the hours and minutes
    if (timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      targetDate.setHours(hours, minutes, 0, 0);
    } else {
      // If no time provided, assume start of day
      targetDate.setHours(0, 0, 0, 0);
    }
    
    const timeDiffMs = targetDate.getTime() - now.getTime();
    const hoursDiff = timeDiffMs / (1000 * 60 * 60);
    
    // Must be in the future (hoursDiff > 0) AND within 24 hours (hoursDiff < 24)
    return hoursDiff > 0 && hoursDiff < 24;
  }

  /**
   * Get the minimum bookable date (24 hours from now)
   * Uses date arithmetic to avoid DST transition issues
   * @returns Date object representing the minimum bookable date
   */
  static getMinimumBookableDate(): Date {
    const now = new Date();
    const minDate = new Date(now.getTime() + (24 * 60 * 60 * 1000));
    console.log('Minimum bookable date is:', minDate);
    return minDate;
  }
}